import { useContext, useEffect, useState } from 'react'

import { format, parse, subYears, addYears } from 'date-fns'
import { useLocation, useNavigate } from 'react-router-dom'
import InputAdornment from '@mui/material/InputAdornment'
import { Controller, useForm } from 'react-hook-form'
import Autocomplete from '@mui/material/Autocomplete'
import { yupResolver } from '@hookform/resolvers/yup'
import SaveIcon from '@mui/icons-material/Save'
import AddIcon from '@mui/icons-material/Add'
import { Grid, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import * as Yup from 'yup'

import { TextFieldCapitalize } from 'src/components/TextFieldCapitalize'
import { TextFieldUppercase } from 'src/components/TextFieldUppercase'
import { TextFieldComponent } from 'src/components/TextFieldComponent'
import {
  useGetIssuingAgencies,
  useGetMaritalStatusTypes,
  useGetNationalities,
  useGetResponsibleRelationships,
  useGetSexTypes,
  useGetStates,
} from 'src/services/backend'
import { ResponsibleCard } from 'src/components/ResponsibleCard'
import { ModalFullScreen } from 'src/components/ModalFullScreen'
import { TypographyComponent } from 'src/components/Typography'
import { TextFieldMask } from 'src/components/TextFieldMask'
import { CheckboxComponent } from 'src/components/Checkbox'
import checklistDocument from 'src/assets/images/data.json'
import { DatePicker } from 'src/components/DatePicker'
import { BoxButtons } from 'src/components/BoxButton'
import { useGetIbgeStates } from 'src/services/ibge'
import { ErrorText } from 'src/components/ErrorText'
import { composePath, isCpfValid } from 'src/utils'
import { emitNotificationEvent } from 'src/events'
import DataContext from 'src/contexts/DataContext'
import { Loading } from 'src/components/Loading'
import { Stepper } from 'src/components/Stepper'
import { Button } from 'src/components/Button'
import * as S from './styles'

const ResponsiblePage = () => {
  const navigate = useNavigate()
  const currentDate = new Date()
  const minAge = 18
  const maxDate = subYears(new Date(), minAge)
  const responsibleIndexFromRemotePage = useLocation()?.state?.responsibleIndex

  const disabledYears: (number | Date)[] = []
  for (let i = 0; i <= minAge; i++) {
    disabledYears.push(addYears(maxDate, i + 1).getFullYear())
  }

  const disableInvalidYears = (date: Date) =>
    disabledYears.includes(date.getFullYear())

  const toasts = {
    errorGetStates: () =>
      emitNotificationEvent({
        id: 'errorGetStates',
        type: 'error',
        title: 'Ops!',
        message: 'Ocorreu um erro ao carregar as opções de "Estado"',
        isIndeterminate: true,
        textButton: 'Entendi',
      }),
    errorToLoadCep: () =>
      emitNotificationEvent({
        id: 'errorToLoadCep',
        type: 'error',
        title: 'Ops',
        message: 'Ocorreu um erro ao consultar o CEP',
        isIndeterminate: true,
        textButton: 'Fechar',
      }),
    InvalidCep: () =>
      emitNotificationEvent({
        id: 'InvalidCep',
        type: 'error',
        title: 'Ops',
        message: 'Você precisa digitar um CEP válido!',
        isIndeterminate: true,
        textButton: 'Fechar',
      }),
    cepNotFound: () =>
      emitNotificationEvent({
        id: 'cepNotFound',
        type: 'error',
        title: 'Ops',
        message: 'CEP não encontrado!',
        isIndeterminate: true,
        textButton: 'Fechar',
      }),
    errorGetSexTypes: () =>
      emitNotificationEvent({
        id: 'errorGetSexTypes',
        type: 'error',
        title: 'Ops!',
        message: 'Ocorreu um erro ao carregar as opções de "Sexo"',
        isIndeterminate: true,
        textButton: 'Entendi',
      }),
    errorGetRelationship: () =>
      emitNotificationEvent({
        id: 'errorGetRelationship',
        type: 'error',
        title: 'Ops',
        message: 'Ocorreu um erro ao carregar as opções de "Parentesco"',
        isIndeterminate: true,
        textButton: 'Fechar',
      }),
  }

  const getSexTypes = useGetSexTypes()

  const relationshipTypes = useGetResponsibleRelationships()

  const validationScheema = Yup.object().shape({
    name: Yup.string().required('Nome do(a) responsável é obrigatório'),
    civil_state: Yup.object()
      .shape({
        pk_id: Yup.number(),
        name: Yup.string(),
      })
      .nullable()
      .required('Estado civil é obrigatório'),
    socialName: Yup.string().nullable(),
    sex: Yup.object()
      .shape({ label: Yup.string() })
      .nullable()
      .required('Sexo é obrigatório'),
    relationship: Yup.object()
      .shape({ id: Yup.number(), name: Yup.string() })
      .nullable()
      .required('Parentesco é obrigatório'),
    birth_date: Yup.date()
      .nullable()
      .required('Data de nascimento é obrigatória')
      .test(
        'is-date-after-today',
        'Data de nascimento não pode ser maior que a data atual',
        (value) => value < currentDate
      )
      .max(maxDate, 'Responsável deve ser maior de 18 anos')
      .typeError('Formato de data inválido'),
    cpf: Yup.string()
      .required('CPF do(a) responsável é obrigatório')
      .test('is-cpf-valid', 'CPF do(a) responsável é inválido', (value) =>
        isCpfValid(value)
      )
      .test(
        'is-cpf-different-than-candidates-one',
        'CPF do(a) responsável não pode ser igual ao do(a) candidato(a)',
        (value) => isCpfDifferentThanCandidatesOne(value)
      )
      .test(
        'is-cpf-unique',
        'CPF do(a) responsável não pode ser igual ao de outro(a)',
        (value) => isCpfUnique(value)
      ),
    is_foreign: Yup.boolean(),
    nationality: Yup.object()
      .shape({
        pk_id: Yup.number(),
        name: Yup.string(),
      })
      .nullable()
      .required('Nacionalidade é obrigatória'),
    document: Yup.string()
      .required('Número do documento é obrigatório')
      .test(
        'is-document-same-number',
        'Documento do responsável não pode ser igual ao do candidato',
        (value) => registration.document !== value
      )
      .matches(/\d/, 'Documento precisa possuir no mínimo um número.'),
    issue_entity: Yup.object()
      .shape({
        pk_id: Yup.number(),
        name: Yup.string(),
      })
      .nullable()
      .required('Órgão expedidor é obrigatório'),
    issue_state: Yup.object()
      .shape({
        pk_id: Yup.number(),
        name: Yup.string(),
      })
      .nullable()
      .when('is_foreign', {
        is: false,
        then: (schema) => schema.required('Estado emissor é obrigatório'),
      }),
    issue_date: Yup.date()
      .nullable()
      .required('Data de emissão é obrigatória')
      .test(
        'issue-date',
        'Data de emissão deve ser maior ou igual a data de nascimento.',
        (value, { parent }) => {
          const { birth_date } = parent
          if (!birth_date || !value) {
            return true
          }

          const formattedBirthDate = new Date(birth_date).setHours(0, 0, 0, 0)
          const formattedIssueDate = new Date(value).setHours(0, 0, 0, 0)

          return formattedIssueDate >= formattedBirthDate
        }
      )
      .max(currentDate, 'Data de emissão não pode ser superior á data atual')
      .typeError('Informe uma data de emissão válida'),
    whatsapp: Yup.string()
      .required('Whatsapp é obrigatório')
      .test(
        'have-length-sufficient',
        'O celular deve conter pelo menos 8 dígitos.',
        (value) => value.replaceAll('_', '')?.length > 13
      ),
    whatsappConfirmation: Yup.string()
      .oneOf([Yup.ref('whatsapp'), ''], 'Confirmação de whatsapp incorreta')
      .required('Confirmação de whatsapp é obrigatória')
      .test(
        'have-length-sufficient',
        'O celular deve conter pelo menos 8 dígitos.',
        (value) => value.replaceAll('_', '')?.length > 13
      ),
    email: Yup.string()
      .trim()
      .email('Informe um e-mail válido')
      .required('E-mail é obrigatório'),
    emailConfirmation: Yup.string()
      .trim()
      .email('Informe um e-mail válido')
      .oneOf([Yup.ref('email'), ''], 'Confirmação de e-mail incorreta')
      .required('Confirmação de e-mail é obrigatória'),
    hasAnotherAddress: Yup.boolean(),
    postal_code: Yup.string().when('hasAnotherAddress', {
      is: true,
      then: (schema) => schema.required('Cep é obrigatório'),
    }),
    street: Yup.string().when('hasAnotherAddress', {
      is: true,
      then: (schema) => schema.required('Nome da rua é obrigatório'),
    }),
    number: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .nullable()
      .when('hasAnotherAddress', {
        is: true,
        then: (schema) =>
          schema
            .positive('Números negativos ou 0 não são aceitos')
            .integer('Apenas números inteiros são aceitos'),
      }),
    complemento: Yup.string().nullable(),
    neighborhood: Yup.string().when('hasAnotherAddress', {
      is: true,
      then: (schema) => schema.required('Bairro é obrigatório'),
    }),
    city: Yup.string().when('hasAnotherAddress', {
      is: true,
      then: (schema) => schema.required('Cidade é obrigatória'),
    }),
    state: Yup.string().when('hasAnotherAddress', {
      is: true,
      then: (schema) => schema.required('Estado é obrigatório'),
    }),
  })

  const defaultValues = {
    name: '',
    civil_state: null,
    socialName: '',
    sex: null,
    relationship: null,
    birth_date: undefined,
    cpf: '',
    is_foreign: false,
    nationality: null,
    document: '',
    issue_entity: null,
    issue_state: null,
    issue_date: undefined,
    whatsapp: '',
    whatsappConfirmation: '',
    email: '',
    emailConfirmation: '',
    hasAnotherAddress: false,
    postal_code: '',
    street: '',
    number: '',
    complemento: '',
    neighborhood: '',
    city: '',
    state: '',
  }

  const defaultAddress = {
    street: '',
    neighborhood: '',
    city: '',
    state: '',
  }

  const {
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    watch,
    reset,
    setValue,
    resetField,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationScheema),
  })

  const [responsibleList, setResposibleList] = useState<any[]>([])
  const [currentResponsibleIndex, setCurrentResponsibleIndex] =
    useState<number>(0)
  const [isResponsableModalOpen, setIsResponsableModalOpen] =
    useState<boolean>(false)
  const [isReadOnlyEnabled, setIsReadOnlyEnabled] = useState<boolean>(false)
  const [disableAddressFields, setDisableAddressFields] =
    useState<boolean>(true)
  const [cepLoading, setCepLoading] = useState<boolean>(false)
  const [cepResponse, setCepResponse] = useState<any>(null)

  const [getIbgeStates, getIbgeStatesResult] = useGetIbgeStates()
  const { registration, setRegistration } = useContext(DataContext)
  const currentStep = registration.getStep(registration, 'responsibles')

  const states = useGetStates()
  const issuingAgencies = useGetIssuingAgencies()
  const nationalities = useGetNationalities()
  const maritalStatusTypes = useGetMaritalStatusTypes()

  // it is actually the responsible modal submit
  const submitForm = (data: any) => {
    const dataFormatted = {
      ...data,
      birth_date: format(data.birth_date, 'yyyy-MM-dd'),
      issue_date: format(data.issue_date, 'yyyy-MM-dd'),
    }
    if (isFormValid()) {
      if (isDirty) {
        const isAdding = currentResponsibleIndex + 1 > responsibleList.length

        if (isAdding)
          setResposibleList((oldList) => [...oldList, dataFormatted])
        else
          setResposibleList((oldList) => {
            const newList = [...oldList]
            newList[currentResponsibleIndex] = dataFormatted
            return newList
          })
      }

      setIsResponsableModalOpen(false)
    }
  }

  const isFormValid = () => isValid || !Object.keys(errors)?.length

  const getStateAcronyms = () =>
    getIbgeStatesResult.data?.map((state: any) => state.sigla) || []

  const handleAddResponsible = () => {
    setFormData(defaultValues)
    setCurrentResponsibleIndex(responsibleList.length)
    setIsReadOnlyEnabled(false)
    setIsResponsableModalOpen(true)
  }

  const handleEditResponsible = (index: number) => {
    setFormData(responsibleList[index])
    setCurrentResponsibleIndex(index)
    setIsReadOnlyEnabled(false)
    setIsResponsableModalOpen(true)
  }

  const handleViewResponsible = (index: number) => {
    setFormData(responsibleList[index])
    setCurrentResponsibleIndex(index)
    setIsReadOnlyEnabled(true)
    setIsResponsableModalOpen(true)
  }

  const handleClickNext = () => {
    if (responsibleList.length > 0) {
      if (isDirty)
        registration.setFormData(setRegistration, 'responsibles', {
          responsibleList,
        })

      navigate(composePath('/confirmacao', `admissao/${registration.course}`))
    }
  }

  const clearAddress = () => {
    Object.keys(defaultAddress).forEach((prop) => {
      //@ts-ignore

      setValue(prop, defaultAddress[prop])
    })
  }

  const getCep = (cep: string) => {
    const translator = {
      bairro: 'neighborhood',
      logradouro: 'street',
      localidade: 'city',
      cep: 'postal_code',
      uf: 'state',
    }
    const data = cep.replace('-', '').replace('_', '')
    const addressFormDataContext = currentStep?.formData

    if (data.length === 8)
      if (cep === addressFormDataContext?.postal_code)
        setFormData(addressFormDataContext)
      else {
        setDisableAddressFields(true)
        setCepLoading(true)
        fetch(`https://viacep.com.br/ws/${data}/json/`)
          .then((res) => res.json())
          .then((obj) => {
            if (obj.erro) {
              clearAddress()
              toasts.cepNotFound()
            }
            Object.keys(obj)
              .filter((prop) => prop in translator)
              .forEach((prop) => {
                //@ts-ignore
                setValue(translator[prop], obj[prop])
              })

            setCepResponse(obj)

            setDisableAddressFields(false)
            setCepLoading(false)

            trigger(['street', 'neighborhood', 'city', 'state'])
          })
          .catch((error) => {
            setDisableAddressFields(false)
            setCepLoading(false)
            toasts.errorToLoadCep()
            if (!disableAddressFields) setDisableAddressFields(true)
          })
      }
    else toasts.InvalidCep()
  }

  const setFormData = (formData: any) => {
    Object.keys(formData).forEach((field) => {
      //@ts-ignore
      setValue(field, formData[field])
    })

    reset(
      {},
      {
        keepErrors: false,
        keepDirty: false,
        keepDirtyValues: false,
        keepValues: true,
        keepDefaultValues: false,
        keepIsSubmitted: true,
        keepTouched: false,
        keepIsValid: true,
        keepSubmitCount: true,
      }
    ) // resetando alguns estados especificos do formulario
  }

  const isCpfDifferentThanCandidatesOne = (cpf: string) =>
    cpf !== registration.cpf

  const isCpfUnique = (cpf: string) =>
    !responsibleList.filter(
      (responsible, index) =>
        responsible.cpf === cpf && index !== currentResponsibleIndex
    ).length

  const resetAddress = () => {
    resetField('postal_code')
    resetField('street')
    resetField('number')
    resetField('neighborhood')
    resetField('city')
    resetField('state')
    resetField('complemento')
  }

  useEffect(() => {
    if (!registration.course)
      navigate(`/admissao/${localStorage.getItem('course')}`)

    if (!!currentStep?.formData) {
      setDisableAddressFields(false)
      setResposibleList(currentStep.formData?.responsibleList || [])
    }

    if (responsibleIndexFromRemotePage && !!currentStep?.formData) {
      setFormData(
        currentStep.formData?.responsibleList[responsibleIndexFromRemotePage]
      )
      setCurrentResponsibleIndex(responsibleIndexFromRemotePage)
      setIsResponsableModalOpen(true)
    }

    getIbgeStates()
  }, [])

  useEffect(() => {
    if (getSexTypes?.error) toasts.errorGetSexTypes()
  }, [getSexTypes?.error])

  useEffect(() => {
    if (getIbgeStatesResult?.error) toasts.errorGetStates()
  }, [getIbgeStatesResult?.error])

  useEffect(() => {
    if (relationshipTypes.error) toasts.errorGetRelationship()
  }, [relationshipTypes.error])

  return (
    <S.ContainerFlex>
      <S.Content>
        <Stepper
          currentStepName={currentStep?.name}
          label={currentStep?.title}
          course={registration.course}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} sm={6}>
            <S.Title>Informe os dados do(a) responsável(a)</S.Title>
            <TypographyComponent>
              Cadastre no mínimo 1 responsável; e, no máximo, 3 responsáveis.
            </TypographyComponent>
          </Grid>
          <S.GridEnd item xs={12} md={6} sm={6}>
            <Button
              variant={'contained'}
              onClick={() => handleAddResponsible()}
              startIcon={<AddIcon />}
              disabled={responsibleList.length >= 3}
            >
              Cadastrar Responsável
            </Button>
          </S.GridEnd>
        </Grid>
        {responsibleList.length ? (
          responsibleList.map((responsible, index) => (
            <ResponsibleCard
              key={index}
              title={`Responsável ${index + 1}`}
              name={responsible.name}
              relationName={responsible.relationship.name}
              handleEditResponsible={() => handleEditResponsible(index)}
              handleViewResponsible={() => handleViewResponsible(index)}
            />
          ))
        ) : (
          <S.InfoFlex>
            <S.ContentFlex>
              <S.LottieAnimation
                animationData={checklistDocument}
                autoplay={true}
                loop={true}
              />
            </S.ContentFlex>
            <S.ContentFlex>
              <S.Title>
                Aqui aparecerá a lista de responsáveis cadastrados(as).
              </S.Title>
              <S.Info>
                Clique no botão acima para cadastrar um(a) responsável.
              </S.Info>
            </S.ContentFlex>
          </S.InfoFlex>
        )}
        <BoxButtons
          onClickPrevius={() =>
            navigate(
              composePath('/pesquisa', `admissao/${registration.course}`)
            )
          }
          onClickNext={() => handleClickNext()}
          disableNextButton={responsibleList.length === 0}
        />
      </S.Content>

      <ModalFullScreen
        isOpen={isResponsableModalOpen}
        onCloseModal={() => setIsResponsableModalOpen(false)}
      >
        <S.ModalBox onSubmit={handleSubmit(submitForm)}>
          <S.Header>
            <S.ModalTitle>Cadastro do Responsável</S.ModalTitle>
          </S.Header>

          <S.ModalDescription>{`Responsável ${
            currentResponsibleIndex + 1
          }`}</S.ModalDescription>

          <Grid container spacing={2}>
            <Grid item sm={6} xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldCapitalize
                    {...field}
                    label={'Nome completo do(a) responsável'}
                    fullWidth
                    error={!!fieldState.error}
                    disabled={isReadOnlyEnabled}
                  />
                )}
              />
              <ErrorText message={errors.name && errors.name.message} />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Controller
                name="civil_state"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    options={maritalStatusTypes?.data?.response || []}
                    getOptionLabel={(nat) => nat.name}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextFieldComponent
                        {...params}
                        label="Estado civil"
                        required
                        fullWidth
                        error={!!fieldState.error}
                      />
                    )}
                  />
                )}
              />
              <ErrorText
                message={errors.civil_state && errors.civil_state.message}
              />
            </Grid>

            <Grid item sm={12} xs={12}>
              <Tooltip title="O nome social é a forma como pessoas transgêneras e não binárias escolhem ser chamadas. Para solicitar a alteração do nome nos registros, entre em contato com a secretaria da escola.">
                <S.InfoSocialName>
                  <InfoIcon fontSize="large" />
                  <p>
                    O nome social é a forma como pessoas transgêneras e não
                    binárias escolhem ser chamadas. Para solicitar a alteração
                    do nome nos registros, entre em contato com a secretaria da
                    escola.
                  </p>
                </S.InfoSocialName>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={6} sm={6}>
              <Controller
                name="sex"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    options={getSexTypes.data?.response || []}
                    getOptionLabel={(sex) => (sex && sex['name']) || ''}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextFieldComponent
                        {...params}
                        label="Sexo"
                        fullWidth
                        error={!!fieldState.error}
                      />
                    )}
                  />
                )}
              />
              <ErrorText message={errors.sex && errors.sex.message} />
            </Grid>
            <Grid item xs={12} md={6} sm={6}>
              <Controller
                name="relationship"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    options={relationshipTypes?.data?.response}
                    getOptionLabel={(relationship) =>
                      (relationship && relationship['name']) || ''
                    }
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextFieldComponent
                        {...params}
                        label="Parentesco"
                        fullWidth
                        error={!!fieldState.error}
                      />
                    )}
                    disabled={isReadOnlyEnabled}
                  />
                )}
              />
              <ErrorText
                message={errors.relationship && errors.relationship.message}
              />
            </Grid>
            <Grid item xs={12} md={6} sm={6}>
              <Controller
                name="birth_date"
                control={control}
                render={({ field, fieldState }) => (
                  <DatePicker
                    {...field}
                    sx={{
                      width: '100%',
                    }}
                    label="Data de nascimento"
                    inputFieldProps={{ error: !!fieldState.error }}
                    shouldDisableYear={disableInvalidYears}
                    disableFuture
                    onChange={(value) => {
                      field.onChange(value)
                      if (control._formValues.issue_date) trigger('issue_date')
                    }}
                    disabled={isReadOnlyEnabled}
                    value={
                      field.value
                        ? parse(field.value, 'yyyy-MM-dd', new Date())
                        : undefined
                    }
                  />
                )}
              />
              <ErrorText
                message={errors.birth_date && errors.birth_date.message}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                name="cpf"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldMask
                    {...field}
                    mask="999.999.999-99"
                    label="CPF do(a) responsável"
                    onChangeMask={field.onChange}
                    error={!!fieldState.error}
                    disabled={isReadOnlyEnabled}
                  />
                )}
              />
              <ErrorText message={errors.cpf && errors.cpf.message} />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Controller
                name="is_foreign"
                control={control}
                render={({ field }) => (
                  <S.FormControl
                    control={
                      <CheckboxComponent
                        onChange={(value) => {
                          field.onChange(value)
                          if (control._formValues.issue_state)
                            trigger('issue_state')
                        }}
                        checked={field.value}
                      />
                    }
                    label={
                      <S.Typograph>RESPONSÁVEL É ESTRANGEIRO(A)</S.Typograph>
                    }
                    disabled={isReadOnlyEnabled}
                  />
                )}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Controller
                name="nationality"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    options={nationalities?.data?.response || []}
                    getOptionLabel={(nat) => nat.name}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextFieldComponent
                        {...params}
                        label="Nacionalidade"
                        required
                        fullWidth
                        error={!!fieldState.error}
                      />
                    )}
                  />
                )}
              />
              <ErrorText
                message={errors.nationality && errors.nationality.message}
              />
            </Grid>

            <Grid container item xs={12} spacing={2}>
              <Grid item lg={3} xs={12}>
                <Controller
                  name="document"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextFieldComponent
                      {...field}
                      fullWidth
                      label={`${
                        watch('is_foreign') ? 'RNE' : 'RG'
                      } do(a) responsável`}
                      error={!!fieldState.error}
                      disabled={isReadOnlyEnabled}
                    />
                  )}
                />
                <ErrorText
                  message={errors.document && errors.document.message}
                />
              </Grid>
              <Grid item lg={3} xs={12}>
                <Controller
                  name="issue_entity"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      disablePortal
                      options={issuingAgencies?.data?.response || []}
                      getOptionLabel={(agency) => agency.acronym}
                      onChange={(_, value) => field.onChange(value)}
                      renderInput={(params) => (
                        <TextFieldComponent
                          {...params}
                          label="Órgão expedidor"
                          fullWidth
                          error={!!fieldState.error}
                        />
                      )}
                    />
                  )}
                />
                <ErrorText
                  message={errors.issue_entity && errors.issue_entity.message}
                />
              </Grid>
              <Grid item lg={3} xs={12}>
                <Controller
                  name="issue_state"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      disablePortal
                      options={states?.data?.response || []}
                      getOptionLabel={(state) => state.acronym}
                      onChange={(_, value) => field.onChange(value)}
                      renderInput={(params) => (
                        <TextFieldComponent
                          {...params}
                          label="Estado emissor"
                          fullWidth
                          error={!!fieldState.error}
                        />
                      )}
                    />
                  )}
                />
                <ErrorText
                  message={errors.issue_state && errors.issue_state.message}
                />
              </Grid>
              <Grid item lg={3} xs={12}>
                <Controller
                  name="issue_date"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      {...field}
                      sx={{
                        width: '100%',
                      }}
                      label="Data de emissão"
                      inputFieldProps={{ error: !!fieldState.error }}
                      disabled={isReadOnlyEnabled}
                      value={
                        field.value
                          ? parse(field.value, 'yyyy-MM-dd', new Date())
                          : undefined
                      }
                    />
                  )}
                />
                <ErrorText
                  message={errors.issue_date && errors.issue_date.message}
                />
              </Grid>
            </Grid>

            <Grid item sm={6} xs={12}>
              <Controller
                name="whatsapp"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldMask
                    {...field}
                    mask="(99) 99999-9999"
                    label="WhatsApp do(a) responsável"
                    onChangeMask={(value) => {
                      field.onChange(value)
                      if (control._formValues.whatsappConfirmation)
                        trigger('whatsappConfirmation')
                    }}
                    error={!!fieldState.error}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <S.WhatsApp />
                        </InputAdornment>
                      ),
                    }}
                    disabled={isReadOnlyEnabled}
                  />
                )}
              />
              <ErrorText message={errors.whatsapp && errors.whatsapp.message} />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                name="whatsappConfirmation"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldMask
                    {...field}
                    mask="(99) 99999-9999"
                    label="Confirme o WhatsApp"
                    onChangeMask={field.onChange}
                    error={!!fieldState.error && !!watch('whatsapp')}
                    onPasteMask={(e) => e.preventDefault()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <S.WhatsApp />
                        </InputAdornment>
                      ),
                    }}
                    disabled={isReadOnlyEnabled || !watch('whatsapp')}
                  />
                )}
              />
              {!!watch('whatsapp') && (
                <ErrorText
                  message={
                    errors.whatsappConfirmation &&
                    errors.whatsappConfirmation.message
                  }
                />
              )}
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    {...field}
                    fullWidth
                    label="E-mail do(a) responsável"
                    error={!!fieldState.error}
                    autoComplete="new-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <S.Email />
                        </InputAdornment>
                      ),
                    }}
                    disabled={isReadOnlyEnabled}
                  />
                )}
              />
              <ErrorText message={errors.email && errors.email.message} />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                name="emailConfirmation"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    {...field}
                    fullWidth
                    label="Confirme o e-mail"
                    error={!!fieldState.error && !!watch('email')}
                    onPaste={(e) => e.preventDefault()}
                    autoComplete="new-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <S.Email />
                        </InputAdornment>
                      ),
                    }}
                    disabled={isReadOnlyEnabled || !watch('email')}
                  />
                )}
              />
              {!!watch('email') && (
                <ErrorText
                  message={
                    errors.emailConfirmation && errors.emailConfirmation.message
                  }
                />
              )}
            </Grid>

            <Grid item sm={12} xs={12}>
              <Controller
                name="hasAnotherAddress"
                control={control}
                render={({ field }) => (
                  <S.FormControl
                    control={
                      <CheckboxComponent
                        onChange={(value) => {
                          field.onChange(value)
                          if (value.target.checked) {
                            resetAddress()
                          }
                        }}
                        checked={field.value}
                      />
                    }
                    label={
                      <S.Typograph>ENDEREÇO DIFERENTE DO CANDIDATO</S.Typograph>
                    }
                    disabled={isReadOnlyEnabled}
                  />
                )}
              />
            </Grid>

            {watch('hasAnotherAddress') && (
              <>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="postal_code"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextFieldMask
                        value={field.value}
                        onChangeMask={field.onChange}
                        onBlurMask={(event) => getCep(event.target.value)}
                        onKeyDownMask={(e) => {
                          if (e.keyCode === 13) {
                            e.preventDefault()
                            getCep(e.target.value)
                          }
                        }}
                        error={!!fieldState.error}
                        mask="99999-999"
                        label="CEP do(a) responsável"
                        variant="outlined"
                        disabled={isReadOnlyEnabled}
                      />
                    )}
                  />
                  <ErrorText
                    message={errors.postal_code && errors.postal_code.message}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="street"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextFieldComponent
                        label="Nome da rua"
                        disabled={isReadOnlyEnabled}
                        value={field.value || ''}
                        error={!!fieldState.error}
                        onChange={field.onChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <ErrorText message={errors.street && errors.street.message} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="number"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextFieldComponent
                        type="number"
                        label="Número"
                        disabled={isReadOnlyEnabled}
                        value={field.value}
                        error={!!fieldState.error}
                        onChange={field.onChange}
                        onKeyDown={(e) => {
                          const re = /[0-9]/
                          if (!re.test(e.key) && e.key != 'Backspace') {
                            e.preventDefault()
                          }
                        }}
                      />
                    )}
                  />
                  <ErrorText message={errors.number && errors.number.message} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="neighborhood"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextFieldComponent
                        label="Bairro"
                        disabled={isReadOnlyEnabled}
                        value={field.value || ''}
                        error={!!fieldState.error}
                        onChange={field.onChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <ErrorText
                    message={errors.neighborhood && errors.neighborhood.message}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextFieldComponent
                        label="Cidade"
                        value={field.value || ''}
                        disabled={isReadOnlyEnabled}
                        error={!!fieldState.error}
                        onChange={field.onChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <ErrorText message={errors.city && errors.city.message} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="state"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextFieldComponent
                        label="Estado (UF)"
                        disabled={isReadOnlyEnabled}
                        value={field.value || ''}
                        error={!!fieldState.error}
                        onChange={field.onChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <ErrorText message={errors.state && errors.state.message} />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="complemento"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextFieldComponent
                        label="Complemento"
                        value={field.value}
                        error={!!fieldState.error}
                        onChange={field.onChange}
                        disabled={isReadOnlyEnabled}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <S.ModalBoxButtons>
            <Button
              variant={'outlined'}
              onClick={() => setIsResponsableModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" endIcon={<SaveIcon />} variant={'contained'}>
              Salvar
            </Button>
          </S.ModalBoxButtons>
        </S.ModalBox>
      </ModalFullScreen>

      <Loading isLoading={cepLoading} />
    </S.ContainerFlex>
  )
}

export { ResponsiblePage }
